import { Earth } from './Earth';
import Phaser from 'phaser';

export class ClassicEarth extends Earth {
  private earthSprite: Phaser.GameObjects.Image | null = null;
  private atmosphereLayer: Phaser.GameObjects.Graphics | null = null;
  private cloudLayer: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    // Call parent constructor with proper parameters
    super(scene, x, y, 100, 0.01, 4, 5000, 0);
    
    // Set specific properties
    this.damageResistance = 0;
    this.specialAbility = 'None';
    
    // Create realistic Earth visuals
    this.createRealisticEarth();
  }

  private createRealisticEarth(): void {
    // Remove default circle
    this.removeAll();
    
    // Create Earth base (realistic colors and patterns)
    const earthBase = this.scene.add.graphics();
    
    // Earth surface with continents
    earthBase.fillStyle(0x1e90ff, 1); // Ocean blue
    earthBase.fillCircle(0, 0, 50);
    
    // Add continents (simplified)
    earthBase.fillStyle(0x228b22, 1); // Forest green
    
    // North America-like continent
    earthBase.fillEllipse(-15, -10, 25, 20);
    
    // Europe/Africa-like continent  
    earthBase.fillEllipse(10, -5, 20, 35);
    
    // Asia-like continent
    earthBase.fillEllipse(20, -25, 30, 15);
    
    // Antarctica-like
    earthBase.fillStyle(0xfffafa, 1); // Snow white
    earthBase.fillEllipse(0, 35, 40, 15);
    
    // Add to container
    this.add(earthBase);
    
    // Create atmosphere layer
    this.createAtmosphereLayer();
    
    // Create cloud layer
    // this.createCloudLayer();
    
    // Create city lights (night side effect)
    this.createCityLights();
  }

  private createAtmosphereLayer(): void {
    this.atmosphereLayer = this.scene.add.graphics();
    
    // Atmospheric glow gradient
    const gradient = this.atmosphereLayer.createGeometryMask();
    
    // Inner atmosphere
    this.atmosphereLayer.fillStyle(0x87ceeb, 0.3);
    this.atmosphereLayer.fillCircle(0, 0, 55);
    
    // Outer atmosphere  
    this.atmosphereLayer.fillStyle(0x4169e1, 0.15);
    this.atmosphereLayer.fillCircle(0, 0, 65);
    
    // Ozone layer effect
    this.atmosphereLayer.lineStyle(2, 0x00bfff, 0.4);
    this.atmosphereLayer.strokeCircle(0, 0, 58);
    
    this.add(this.atmosphereLayer);
    
    // Breathing animation
    this.scene.tweens.add({
      targets: this.atmosphereLayer,
      scaleX: 1.05,
      scaleY: 1.05,
      alpha: 0.7,
      duration: 4000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createCloudLayer(): void {
    this.cloudLayer = this.scene.add.graphics();
    
    // Create cloud patterns
    this.cloudLayer.fillStyle(0xffffff, 0.6);
    
    // Swirling cloud patterns
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8;
      const x = Math.cos(angle) * (20 + Math.random() * 15);
      const y = Math.sin(angle) * (20 + Math.random() * 15);
      const size = 8 + Math.random() * 6;
      
      this.cloudLayer.fillEllipse(x, y, size, size * 0.6);
    }
    
    // Hurricane-like formation
    this.cloudLayer.fillStyle(0xf0f8ff, 0.4);
    this.cloudLayer.fillEllipse(15, -20, 18, 12);
    
    this.add(this.cloudLayer);
    
    // Slow rotation for cloud movement
    this.scene.tweens.add({
      targets: this.cloudLayer,
      rotation: Math.PI * 2,
      duration: 60000, // 1 minute rotation
      repeat: -1,
      ease: 'Linear'
    });
  }

  private createCityLights(): void {
    // Add tiny city lights on the night side
    const cityLights = this.scene.add.graphics();
    
    cityLights.fillStyle(0xffff00, 0.8);
    
    // Random city light points
    for (let i = 0; i < 15; i++) {
      const angle = Math.random() * Math.PI * 2;
      const radius = 25 + Math.random() * 20;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      
      // Only on the "night" side (right side)
      if (x > 0) {
        cityLights.fillCircle(x, y, 0.5);
      }
    }
    
    this.add(cityLights);
    
    // Twinkling effect
    this.scene.tweens.add({
      targets: cityLights,
      alpha: 0.3,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  override useSpecialAbility(): void {
    // No special ability for classic earth
  }

  override activateAbility(): void {
    // Classic Earth doesn't have special ability
    console.log('Classic Earth activated - no special ability');
  }

  protected getAtmosphereColor(): number {
    return 0x87ceeb; // Sky blue - realistic atmosphere
  }

  protected getEnergyColor(): number {
    return 0x00bfff; // Deep sky blue  
  }

  protected getParticleColor(): number {
    return 0x4169e1; // Royal blue
  }

  protected getShieldColor(): number {
    return 0x1e90ff; // Dodger blue
  }

  protected getCoreColor(): number {
    return 0x0000cd; // Medium blue
  }

  override upgrade(): void {
    this.level++;
    this.baseMaxHealth += 200;
    this.recalculateStats();
    this.health = this.effectiveMaxHealth;
    this.towerLimit += 1;
  }
} 